import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Divider,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link
} from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../Component/LanguageProvider';
import { useNavigate } from 'react-router-dom';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BalanceIcon from '@mui/icons-material/Balance';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LicenseIcon from '@mui/icons-material/AssignmentTurnedIn';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';

export const AboutUsPage = () => {
  const { currentLang, direction, isRTL } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Scroll animation
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Company information - Updated with correct name (Almaryah with h)
  const companyInfo = {
    en: {
      name: 'Almaryah Legal Consultancy',
      license: 'Licensed by Creative City – Fujairah',
      licenseDesc: 'Licensed to provide legal consultancy services, which includes offering legal advice and related administrative services without practicing law or judicial representation within the UAE.',
      address: 'Creative City – Fujairah, UAE',
      description: 'A licensed company at Creative City – Fujairah to provide legal consultancy services, which includes offering legal advice and related administrative services without practicing law or judicial representation within the UAE.',
      commitment: 'Almaryah Legal Consultancy is committed to operating in accordance with the license issued by Creative City – Fujairah, and in compliance with federal and local regulations in the UAE. The company confirms that it does not practice law or any activity requiring external approvals from judicial authorities or the Ministry of Justice, and its services are limited to legal consultations and administrative services only without client representation or court litigation.',
      heroTitle: 'Your Trusted Legal Partner',
      heroSubtitle: 'Excellence in Legal Consultancy Since 2015'
    },
    ar: {
      name: 'الماريه للإستشارات القانونيه',
      license: 'مرخصة من Creative City – الفجيرة',
      licenseDesc: 'مرخصة لتقديم خدمات الاستشارات القانونية، والتي تشمل تقديم المشورة القانونية والخدمات الإدارية ذات العلاقة بدون ممارسة أعمال المحاماة أو التمثيل القضائي داخل دولة الإمارات العربية المتحدة.',
      address: 'Creative City – الفجيرة، الإمارات العربية المتحدة',
      description: 'شركة مرخصة لدى Creative City – الفجيرة لتقديم خدمات الاستشارات القانونية، والتي تشمل تقديم المشورة القانونية والخدمات الإدارية ذات العلاقة بدون ممارسة أعمال المحاماة أو التمثيل القضائي داخل دولة الإمارات العربية المتحدة.',
      commitment: 'تلتزم الماريه للإستشارات القانونيه بممارسة نشاطها وفق ما هو مسموح به في الرخصة الصادرة من Creative City – الفجيرة، وبما يتوافق مع الأنظمة الاتحادية والمحلية في دولة الإمارات العربية المتحدة. وتؤكد الشركة أنها لا تمارس أعمال المحاماة أو أي نشاط يتطلب موافقات خارجية من الجهات القضائية أو وزارة العدل، وأن خدماتها تقتصر على الاستشارات القانونية والخدمات الإدارية فقط دون تمثيل العملاء أو الترافع أمام المحاكم.',
      heroTitle: 'شريكك الموثوق في الاستشارات القانونية',
      heroSubtitle: 'التميز في الاستشارات القانونية منذ ٢٠١٥'
    }
  };

  // Company stats - Updated with correct terminology
  const stats = {
    en: [
      { number: '15+', label: 'Years of Experience', icon: VerifiedIcon },
      { number: '500+', label: 'Consultations Handled', icon: GavelIcon },
      { number: '50+', label: 'Expert Consultants', icon: GroupIcon },
      { number: '98%', label: 'Client Satisfaction', icon: EmojiEventsIcon }
    ],
    ar: [
      { number: '١٥+', label: 'سنوات خبرة', icon: VerifiedIcon },
      { number: '٥٠٠+', label: 'استشارة تم التعامل معها', icon: GavelIcon },
      { number: '٥٠+', label: 'مستشار خبير', icon: GroupIcon },
      { number: '٩٨٪', label: 'رضا العملاء', icon: EmojiEventsIcon }
    ]
  };

  // Core values
  const values = {
    en: [
      { 
        title: 'Excellence', 
        description: 'We strive for excellence in every consultation we provide, ensuring the highest quality of legal consultancy services.', 
        icon: EmojiEventsIcon 
      },
      { 
        title: 'Integrity', 
        description: 'Honest and transparent legal advice you can trust. We maintain the highest ethical standards in all our dealings.', 
        icon: BalanceIcon 
      },
      { 
        title: 'Efficiency', 
        description: 'Fast and reliable service to save your time and resources.', 
        icon: SpeedIcon 
      },
      { 
        title: 'Compliance', 
        description: 'Fully compliant with UAE federal and local regulations and Creative City license requirements.', 
        icon: VerifiedIcon 
      }
    ],
    ar: [
      { 
        title: 'التميز', 
        description: 'نسعى للتميز في كل استشارة نقدمها، مما يضمن أعلى جودة من الخدمات الاستشارية القانونية.', 
        icon: EmojiEventsIcon 
      },
      { 
        title: 'النزاهة', 
        description: 'استشارات قانونية صادقة وشفافة يمكنك الوثوق بها. نحافظ على أعلى المعايير الأخلاقية.', 
        icon: BalanceIcon 
      },
      { 
        title: 'الكفاءة', 
        description: 'خدمة سريعة وموثوقة لتوفير وقتك ومواردك.', 
        icon: SpeedIcon 
      },
      { 
        title: 'الامتثال', 
        description: 'ملتزمون بالكامل باللوائح الاتحادية والمحلية في الإمارات ومتطلبات ترخيص Creative City.', 
        icon: VerifiedIcon 
      }
    ]
  };

  const currentInfo = companyInfo[currentLang];
  const currentStats = stats[currentLang];
  const currentValues = values[currentLang];

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Hero Section with Background Image */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '80vh' }, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: 'white',
          mb: { xs: 4, md: 8 },
          backgroundImage: 'url("https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=2")',
          backgroundSize: 'cover',
          backgroundAttachment: 'scroll',
          backgroundPosition: 'center 80%', 
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.7) 0%, rgba(118, 75, 162, 0.7) 100%)',
            zIndex: 1
          }
        }}
      >
        {/* Animated Background Elements */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 45, 0],
            opacity: [0.15, 0.25, 0.15]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            top: '5%',
            right: isRTL ? 'auto' : '5%',
            left: isRTL ? '5%' : 'auto',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />

        <motion.div
          animate={floatingAnimation}
          style={{
            position: 'absolute',
            bottom: '10%',
            left: isRTL ? 'auto' : '5%',
            right: isRTL ? '5%' : 'auto',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          >
            {/* Breadcrumbs */}
            <Breadcrumbs 
              aria-label="breadcrumb"
              sx={{ 
                justifyContent: 'center', 
                display: 'flex',
                mb: 3,
                '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' }
              }}
            >
              <Link
                component="button"
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  '&:hover': { color: 'white' }
                }}
              >
                <HomeIcon sx={{ fontSize: 16 }} />
                {currentLang === 'ar' ? 'الرئيسية' : 'Home'}
              </Link>
              <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
                {currentLang === 'ar' ? 'من نحن' : 'About Us'}
              </Typography>
            </Breadcrumbs>

            <Typography
              variant="h2"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              {currentInfo.heroTitle}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                color: 'rgba(255,255,255,0.9)',
                mb: 4,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              {currentInfo.heroSubtitle}
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/#contact')}
              sx={{
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                fontWeight: 600,
                borderRadius: '50px',
                boxShadow: '0 8px 20px rgba(254,107,139,0.3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                  boxShadow: '0 12px 25px rgba(254,107,139,0.4)',
                }
              }}
            >
              {currentLang === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pb: 8 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Company Info Card */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                mb: 6,
                background: 'white',
                borderRadius: '20px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(102,126,234,0.1) 0%, rgba(102,126,234,0) 70%)',
                borderRadius: '50%',
                transform: 'translate(30%, -30%)'
              }} />
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                    <BusinessIcon sx={{ color: '#667eea', fontSize: 40, mr: direction === 'rtl' ? 0 : 2, ml: direction === 'rtl' ? 2 : 0 }} />
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                        fontWeight: 'bold',
                        color: '#1a1a2e'
                      }}
                    >
                      {currentInfo.name}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                    <LocationOnIcon sx={{ color: '#e83e8c', fontSize: 20, mr: direction === 'rtl' ? 0 : 1, ml: direction === 'rtl' ? 1 : 0 }} />
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                        color: '#666'
                      }}
                    >
                      {currentInfo.address}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1, justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start' }}>
                    <LicenseIcon sx={{ color: '#20c997', fontSize: 20 }} />
                    <Chip
                      label={currentInfo.license}
                      size="medium"
                      sx={{
                        bgcolor: '#20c997',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.9rem'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      color: '#4a4a4a',
                      lineHeight: 1.8,
                      mb: 2,
                      fontSize: '1.1rem',
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    {currentInfo.description}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>

          {/* Stats Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h3"
              align="center"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                fontWeight: 'bold',
                color: '#1a1a2e',
                mb: 4
              }}
            >
              {currentLang === 'ar' ? 'أرقامنا' : 'Our Numbers'}
            </Typography>
            <Grid container spacing={4} sx={{ mb: 6 }}>
              {currentStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div
                      whileHover={{ scale: 1.05, y: -10 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{ height: '100%' }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          borderRadius: '15px',
                          height: '100%',
                          minHeight: 220,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <Icon sx={{ fontSize: 50, mb: 2 }} />
                        <Typography
                          variant="h3"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            fontWeight: 'bold',
                            mb: 1
                          }}
                        >
                          {stat.number}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter'
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>

          {/* Vision & Mission */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={4} sx={{ mb: 6 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    minHeight: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      mb: 3,
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    {currentLang === 'ar' ? 'رؤيتنا' : 'Our Vision'}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      lineHeight: 1.8,
                      fontSize: '1.1rem',
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    {currentLang === 'ar'
                      ? 'أن نكون الشريك القانوني الأول في المنطقة، نتميز بالتميز والنزاهة والابتكار في تقديم الاستشارات القانونية.'
                      : 'To be the leading legal partner in the region, distinguished by excellence, integrity, and innovation in legal consultancy.'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    minHeight: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      mb: 3,
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    {currentLang === 'ar' ? 'مهمتنا' : 'Our Mission'}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      lineHeight: 1.8,
                      fontSize: '1.1rem',
                      textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}
                  >
                    {currentLang === 'ar'
                      ? 'تقديم استشارات قانونية متميزة وخدمات إدارية عالية الجودة مع الالتزام بأعلى المعايير المهنية والأخلاقية.'
                      : 'Providing distinguished legal advice and high-quality administrative services while adhering to the highest professional and ethical standards.'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </motion.div>

          {/* Core Values */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h3"
              align="center"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                fontWeight: 'bold',
                color: '#1a1a2e',
                mb: 4
              }}
            >
              {currentLang === 'ar' ? 'قيمنا' : 'Our Values'}
            </Typography>
            <Grid container spacing={4} sx={{ mb: 6 }}>
              {currentValues.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div
                      whileHover={{ scale: 1.05, y: -10 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{ height: '100%' }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          height: '100%',
                          minHeight: 320,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          background: 'white',
                          borderRadius: '15px',
                          border: '1px solid rgba(102,126,234,0.2)'
                        }}
                      >
                        <Box>
                          <Avatar
                            sx={{
                              width: 80,
                              height: 80,
                              bgcolor: 'transparent',
                              border: '2px solid #667eea',
                              margin: '0 auto 20px',
                              '& svg': {
                                fontSize: 40,
                                color: '#667eea'
                              }
                            }}
                          >
                            <Icon />
                          </Avatar>
                          
                          <Typography
                            variant="h5"
                            sx={{
                              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                              fontWeight: 'bold',
                              color: '#1a1a2e',
                              mb: 2
                            }}
                          >
                            {value.title}
                          </Typography>
                        </Box>
                        
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            color: '#666',
                            lineHeight: 1.8
                          }}
                        >
                          {value.description}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>

          {/* Commitment Statement - Legal Disclaimer */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '15px',
                border: '1px solid rgba(102,126,234,0.2)'
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: '#4a4a4a',
                  lineHeight: 1.8,
                  fontSize: '1rem',
                  fontStyle: 'italic',
                  textAlign: direction === 'rtl' ? 'right' : 'left'
                }}
              >
                {currentInfo.commitment}
              </Typography>
            </Paper>
          </motion.div>

          {/* Back to Home Button */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon sx={{ transform: direction === 'rtl' ? 'rotate(180deg)' : 'none' }} />}
                onClick={() => navigate('/')}
                sx={{
                  color: '#667eea',
                  borderColor: '#667eea',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 600,
                  borderRadius: '50px',
                  '&:hover': {
                    borderColor: '#764ba2',
                    backgroundColor: 'rgba(118,75,162,0.1)'
                  }
                }}
              >
                {currentLang === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutUsPage;